import { useHttp } from "../http/useHttp";
import { useAxiosQuery, UseAxiosQueryOptions } from "../http/useAxiosQuery";
import { useMemo } from "react";
import { useDefaultError } from "@synota-io/synota-shared-ui";
import { paths } from "../generated-types";

type ServiceChargeCounterpartiesOperation =
  paths["/dashboard/users/{api_token_uuid}/service-charges/counterparties"]["post"];
type UseServiceChargeCounterpartiesResponse =
  ServiceChargeCounterpartiesOperation["responses"][200]["content"]["application/json"];
type UseServiceChargeCounterpartiesRequestBody =
  ServiceChargeCounterpartiesOperation["requestBody"]["content"]["application/json"];
type ServiceChargeCounterpartiesPath = ServiceChargeCounterpartiesOperation["parameters"]["path"];

export type IServiceChargeCounterparty = UseServiceChargeCounterpartiesResponse["items"][number];
export type UseServiceChargeCounterpartiesInput = UseServiceChargeCounterpartiesRequestBody & {
  user_uuid: ServiceChargeCounterpartiesPath["api_token_uuid"];
};

export const useServiceChargeCounterparties = (
  input: UseServiceChargeCounterpartiesInput = { user_uuid: "" },
  options: Omit<
    UseAxiosQueryOptions<UseServiceChargeCounterpartiesResponse>,
    "queryKey" | "queryFn"
  > = {},
) => {
  const { client } = useHttp();

  const { status, isPending, isFetching, isLoading, data, error, refetch } =
    useAxiosQuery<UseServiceChargeCounterpartiesResponse>({
      queryKey: ["user-service-charge-counterparties", input],
      queryFn: ({ signal }) => {
        return client.post(`/users/${input.user_uuid}/service-charges/counterparties`, input, {
          signal,
        });
      },
      enabled: options.enabled !== false && !!input.user_uuid,
      ...options,
    });

  const [counterparties, hasNextPage] = useMemo(() => {
    if (!data) {
      return [[], false];
    }
    return [data.data.items, data.data.has_next_page];
  }, [data]);

  useDefaultError(error);

  return {
    counterparties,
    hasNextPage,
    error,
    isLoading,
    isFetching,
    isPending,
    status,
    refetch,
  };
};

export type UseServiceChargeCounterpartiesReturn = ReturnType<
  typeof useServiceChargeCounterparties
>;
